import { useParams, Link } from "react-router-dom";
import { T } from "@/components/T";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/context/LanguageContext";
import { UPCOMING, UPCOMING_BY_SLUG } from "@/lib/upcoming";
import { MandalaMotif } from "@/components/MotifBackground";
import {
  GraduationCap, Video, Flame, Sunrise, AudioLines, HandCoins,
  HeartHandshake, Repeat, MessagesSquare, HelpCircle, Baby, BookMarked,
  ArrowLeft, ArrowRight, Sparkles,
} from "lucide-react";

const ICONS = {
  GraduationCap, Video, Flame, Sunrise, AudioLines, HandCoins,
  HeartHandshake, Repeat, MessagesSquare, HelpCircle, Baby, BookMarked,
};

export default function ComingSoon({ slug: slugProp }) {
  const params = useParams();
  const { pick } = useLang();
  const slug = slugProp || params.slug;
  const feature = UPCOMING_BY_SLUG[slug];

  if (!feature) {
    return (
      <div className="mx-auto max-w-xl px-5 py-32 text-center">
        <p className="font-serif-latin text-2xl text-ink"><T>Not found</T></p>
        <Link to="/coming-soon" className="mt-4 inline-block text-saffron"><T>View what's coming</T></Link>
      </div>
    );
  }

  const Icon = ICONS[feature.icon] || Sparkles;
  const others = UPCOMING.filter((u) => u.slug !== feature.slug).slice(0, 6);

  return (
    <>
      <Helmet>
        <title>{`${feature.en} — Coming Soon · Dharmartha`}</title>
        <meta name="description" content={feature.desc_en} />
      </Helmet>

      <section className="relative flex min-h-[78vh] items-center overflow-hidden bg-[var(--parchment)]">
        <MandalaMotif className="pointer-events-none absolute -right-32 -top-24 hidden h-[560px] w-[560px] text-[var(--gold)] opacity-[0.05] md:block" />
        <MandalaMotif className="pointer-events-none absolute -bottom-40 -left-40 hidden h-[440px] w-[440px] text-[var(--saffron)] opacity-[0.035] md:block" />

        <div className="relative z-[2] mx-auto w-full max-w-3xl px-5 py-20 text-center md:px-8">
          <span className="mx-auto mb-8 flex h-16 w-16 items-center justify-center border border-line bg-surface text-saffron shadow-sm">
            <Icon className="h-7 w-7" />
          </span>

          <p className="overline-label mb-4 inline-flex items-center gap-2 text-gold">
            <span className="h-px w-6 bg-[var(--gold)]" />
            {pick("शीघ्र आ रहा है", "Coming Soon")}
            <span className="h-px w-6 bg-[var(--gold)]" />
          </p>

          <h1 className="font-deva text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl" data-testid="coming-soon-title">
            {feature.hi}
          </h1>
          <p className="mt-3 font-serif-latin text-xl text-ink-2 sm:text-2xl">{feature.en}</p>

          <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-ink-2">
            {pick(feature.desc_hi, feature.desc_en)}
          </p>

          <div className="mt-8 inline-flex items-center gap-2 border border-line bg-surface px-4 py-2 text-xs uppercase tracking-[0.14em] text-ink-3">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--saffron)] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--saffron)]" />
            </span>
            {pick("निर्माणाधीन", "In development")}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/explore" data-testid="coming-soon-explore" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
              {pick("ज्ञान का अन्वेषण करें", "Explore Dharmartha")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/support" data-testid="coming-soon-support" className="inline-flex items-center gap-2 border border-line px-6 py-3 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
              {pick("इस कार्य में सहयोग करें", "Support this work")}
            </Link>
          </div>
        </div>
      </section>

      {/* Also on the way */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-5xl px-5 py-14 md:px-8">
          <div className="mb-6 flex items-center gap-3">
            <span className="h-px w-8 bg-[var(--gold)]" />
            <h2 className="overline-label text-ink-3">{pick("और भी आ रहा है", "Also on the way")}</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {others.map((u) => {
              const OI = ICONS[u.icon] || Sparkles;
              return (
                <Link key={u.slug} to={u.path} data-testid={`upcoming-chip-${u.slug}`} className="group flex items-center gap-3 border border-line bg-parchment p-4 transition-colors hover:border-saffron">
                  <OI className="h-4 w-4 shrink-0 text-saffron" />
                  <span className="flex-1 text-sm text-ink-2 transition-colors group-hover:text-ink">{pick(u.hi, u.en)}</span>
                </Link>
              );
            })}
          </div>
          <Link to="/coming-soon" className="mt-8 inline-flex items-center gap-1.5 text-sm text-saffron">
            <ArrowLeft className="h-4 w-4" /> {pick("सभी आगामी सुविधाएँ", "See everything that's coming")}
          </Link>
        </div>
      </section>
    </>
  );
}
